import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

/**
 * BreadcrumbService — centralized, route-driven breadcrumb trail.
 *
 * Single source of truth for the application breadcrumb bar. It listens to the
 * router and, on every navigation, walks the activated-route snapshot tree from
 * root to leaf, accumulating the absolute URL segment by segment and reading the
 * `data['breadcrumb']` declaration of each matched route:
 *   - `string`                          -> a static crumb whose label is the i18n key.
 *   - `{ dynamic: true; param: 'x' }`   -> a dynamic crumb whose label is resolved
 *                                          later via {@link setDynamicLabel} keyed by
 *                                          the route param value (e.g. an entity id).
 *   - absent                            -> no crumb is emitted, but the URL keeps
 *                                          accumulating so deeper crumbs stay correct.
 *
 * A `home` crumb is always prepended, so {@link crumbs} never goes below length 1.
 *
 * Dynamic labels are stored in a private signal so that resolving an entity name
 * (after an async fetch) re-renders OnPush consumers. Stale labels are purged on
 * every rebuild so leaving a detail page does not leak the previous entity name.
 *
 * This is infrastructure only: routes declare `data.breadcrumb`, feature pages
 * call {@link setDynamicLabel}, and the shared breadcrumbs component renders
 * {@link crumbs}. Mirrors the modern injection style used across core services.
 */

/** Route-level breadcrumb declaration placed under `data.breadcrumb`. */
export type BreadcrumbRouteConfig = string | { dynamic: true; param: string };

export interface Crumb {
  /** Absolute URL accumulated up to this segment (e.g. '/properties/list'). */
  url: string;
  /** When true the label is resolved from the dynamic label map. */
  dynamic: boolean;
  /** i18n key for static segments — e.g. 'breadcrumbs.properties'. */
  key?: string;
  /** For dynamic crumbs: the route param value used as the resolution key (e.g. the id). */
  paramValue?: string;
  /** Optional icon — e.g. 'mat:home' for the home crumb. */
  icon?: string;
}

const HOME_CRUMB: Crumb = {
  url: '/',
  dynamic: false,
  key: 'breadcrumbs.home',
  icon: 'mat:home'
};

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  /** The current breadcrumb trail. Always contains at least the home crumb. */
  readonly crumbs = signal<Crumb[]>([HOME_CRUMB]);

  /** Resolved labels for dynamic crumbs, keyed by route param value. */
  private readonly dynamicLabels = signal<Map<string, string>>(new Map());

  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Structural URL segments that are navigation plumbing, never a page the user
   * thinks of as a "section" — they must not auto-derive a crumb.
   */
  private static readonly SKIP_SEGMENTS = new Set([
    'list', 'detail', 'create', 'edit', 'add', 'view', 'new'
  ]);

  /**
   * Top-level areas that already curate their own breadcrumbs (explicit
   * `data.breadcrumb`). Auto-derivation is disabled there so we don't inject
   * generic labels into hand-tuned trails.
   */
  private static readonly AUTO_EXCLUDED_AREAS = new Set([
    'admin', 'superadmin', 'agency', 'agency-accept', 'syndic', 'syndic-accept',
    'provider', 'tenant', 'auth', 'onboarding'
  ]);

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.rebuild());

    // Auto-derived crumbs depend on translations being loaded; rebuild once the
    // active/default language (re)loads so gated labels appear and stay in sync.
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.rebuild());
    this.translate.onFallbackLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.rebuild());

    // Emit once for the URL the app booted on (no NavigationEnd fires for it).
    this.rebuild();
  }

  /**
   * Resolve (or update) the label for a dynamic crumb keyed by its route param
   * value. Idempotent: a no-op when the label is already set to the same value.
   */
  setDynamicLabel(paramValue: string, label: string): void {
    if (!paramValue) {
      return;
    }
    if (this.dynamicLabels().get(paramValue) === label) {
      return;
    }
    const next = new Map(this.dynamicLabels());
    next.set(paramValue, label);
    this.dynamicLabels.set(next);
  }

  /** Read the resolved label for a dynamic crumb. Empty string when unresolved. */
  getDynamicLabel(paramValue: string | undefined): string {
    if (!paramValue) {
      return '';
    }
    return this.dynamicLabels().get(paramValue) ?? '';
  }

  /** Walk the snapshot tree and rebuild the breadcrumb trail + purge stale labels. */
  private rebuild(): void {
    const crumbs: Crumb[] = [HOME_CRUMB];
    const activeParamValues = new Set<string>();

    let route: ActivatedRouteSnapshot | null = this.router.routerState?.snapshot?.root ?? null;
    let accumulatedUrl = '';
    let firstSegment: string | null = null;

    while (route) {
      const segments = route.url.map((segment) => segment.path).filter((path) => path.length > 0);
      if (segments.length > 0) {
        accumulatedUrl += '/' + segments.join('/');
        if (firstSegment === null) {
          firstSegment = segments[0];
        }
      }
      const url = accumulatedUrl.length > 0 ? accumulatedUrl : '/';

      // The route's OWN declaration, not `route.data` — that one is the resolved
      // data, which Angular inherits from an ancestor whenever the parent is
      // component-less (the `contracts` wrapper is exactly that). Reading it made
      // a child re-emit its parent's crumb, so `/contracts/list` rendered
      // "Baux · Baux". `routeConfig.data` is the literal object the route
      // declares, so no inheritance can leak into it.
      const config = route.routeConfig?.data?.['breadcrumb'] as BreadcrumbRouteConfig | undefined;
      if (typeof config === 'string') {
        crumbs.push({ url, dynamic: false, key: config });
      } else if (config && config.dynamic === true) {
        const paramValue = route.params[config.param] ?? '';
        crumbs.push({ url, dynamic: true, paramValue });
        if (paramValue) {
          activeParamValues.add(paramValue);
        }
      } else if (segments.length > 0) {
        // No explicit declaration: auto-derive a crumb from the segment so every
        // sub-page/tab is reflected in the trail without per-route wiring.
        const key = this.autoDerivedKey(route, segments, firstSegment);
        if (key) {
          crumbs.push({ url, dynamic: false, key });
        }
      }

      route = route.firstChild;
    }

    this.purgeStaleLabels(activeParamValues);
    this.crumbs.set(crumbs);
  }

  /**
   * Best-effort i18n key for a route that lacks an explicit `data.breadcrumb`.
   * Returns null (no crumb) for excluded areas, structural/parameterised
   * segments, or when no matching translation exists — so we never render a raw
   * key. Checks the top-level `breadcrumbs.*` namespace first, then the shared
   * `breadcrumbs.sections.*` tab vocabulary.
   */
  private autoDerivedKey(
    route: ActivatedRouteSnapshot,
    segments: string[],
    firstSegment: string | null
  ): string | null {
    if (firstSegment && BreadcrumbService.AUTO_EXCLUDED_AREAS.has(firstSegment)) {
      return null;
    }
    const segment = segments[segments.length - 1];
    if (!segment || BreadcrumbService.SKIP_SEGMENTS.has(segment)) {
      return null;
    }
    // Parameterised routes carry a value (id) in their segment, not a stable
    // label — those are handled by explicit `{ dynamic }` declarations only.
    const configuredPath = route.routeConfig?.path;
    if (configuredPath && configuredPath.includes(':')) {
      return null;
    }
    const camel = segment.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
    for (const key of [`breadcrumbs.${camel}`, `breadcrumbs.sections.${camel}`]) {
      if (this.translationExists(key)) {
        return key;
      }
    }
    return null;
  }

  /** True when `key` resolves to a real, non-empty translation (not the key itself). */
  private translationExists(key: string): boolean {
    const value = this.translate.instant(key);
    return typeof value === 'string' && value.length > 0 && value !== key;
  }

  /** Drop resolved labels whose param value is no longer part of the active trail. */
  private purgeStaleLabels(activeParamValues: Set<string>): void {
    const current = this.dynamicLabels();
    let changed = false;
    const next = new Map<string, string>();
    for (const [paramValue, label] of current) {
      if (activeParamValues.has(paramValue)) {
        next.set(paramValue, label);
      } else {
        changed = true;
      }
    }
    if (changed) {
      this.dynamicLabels.set(next);
    }
  }
}
